'use client';

import { useState } from 'react';
import { Check, ImageOff } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleActions, BubbleRows, type BubbleRow } from '@/components/chat/BubbleBlock';
import { CHAT_TAP_OFFLINE, saveFromChat } from '@/lib/chat/block-actions';
import { jobPhotoThumbUrl } from '@/lib/uploads/job-photo-paths';
import {
  REPORT_PHOTO_MAX,
  addLabel,
  mergeReportPhotos,
  normaliseReportPhotos,
  type PhotoPickerBlock,
  type PhotoWhen,
  type ReportPhoto,
} from '@/lib/chat/blocks/photo-picker';

/**
 * A job's photos in the bubble, and "add these to the report".
 *
 * A tap toggles a pick; Before / After says which strip the picks go in; the
 * orange tap writes them onto the DRAFT job report through the job sheet's own
 * PATCH (`saveFromChat`), which does its own permission check. Nothing here
 * reaches the customer: the report is still a draft afterwards, and sending it
 * is a separate act on the job.
 *
 * 🚨 The add reads the sheet FRESH, merges, then writes. The PATCH replaces the
 * whole photo list, and the engineer may have picked photos on site since this
 * reply was drawn — writing only ours would quietly take his off.
 *
 * Tiles are addressed by PATH and their image is the authenticated app route,
 * built here at render, so a stored reply redrawn later is gated on whoever
 * is looking at it now and never holds an expired link.
 */

export interface PhotoPickerProps {
  block: PhotoPickerBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

type ReadSheet = { ok: true; photos: ReportPhoto[] } | { ok: false; message: string };

async function readSheetPhotos(sheetId: string, document: string): Promise<ReadSheet> {
  try {
    const res = await fetch(`/api/job-sheets/${encodeURIComponent(sheetId)}`);
    const body = await res.json().catch(() => ({})) as {
      error?: unknown;
      sheet?: { status?: string; draftContent?: { photoPaths?: unknown } };
    };
    if (!res.ok) {
      return {
        ok: false,
        message: typeof body.error === 'string' && body.error.trim()
          ? body.error
          : "That didn't work, so nothing has changed. Give it another go in a minute.",
      };
    }
    if (body.sheet?.status !== 'draft') {
      return { ok: false, message: `The ${document} on this job is already finished, so photos can't be added to it from here.` };
    }
    return { ok: true, photos: normaliseReportPhotos(body.sheet.draftContent?.photoPaths) };
  } catch {
    return { ok: false, message: CHAT_TAP_OFFLINE };
  }
}

export function PhotoPicker({ block, live, onOpen }: PhotoPickerProps) {
  const target = block.target;
  const open = target.state === 'open' ? target : null;
  const document = open?.document ?? 'report';

  const [picked, setPicked] = useState<string[]>([]);
  const [when, setWhen] = useState<PhotoWhen>('after');
  const [onReport, setOnReport] = useState<ReportPhoto[]>(open?.alreadyOn ?? []);
  const [broken, setBroken] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const onReportByPath = new Map(onReport.map(p => [p.path, p.when]));
  const room = Math.max(0, REPORT_PHOTO_MAX - onReport.length);
  const atRoom = picked.length >= room;

  function toggle(path: string) {
    setError(null);
    setPicked(prev => (prev.includes(path) ? prev.filter(p => p !== path) : prev.length >= room ? prev : [...prev, path]));
  }

  async function add() {
    if (!open || picked.length === 0) return;
    setBusy(true);
    setError(null);
    const read = await readSheetPhotos(open.sheetId, document);
    if (!read.ok) {
      setBusy(false);
      setError(read.message);
      return;
    }
    const merged = mergeReportPhotos(read.photos, picked, when, document);
    if (!merged.ok) {
      setOnReport(read.photos);
      setBusy(false);
      setError(merged.message);
      return;
    }
    const out = await saveFromChat('PATCH', `/api/job-sheets/${encodeURIComponent(open.sheetId)}`, { photoPaths: merged.photos });
    setBusy(false);
    if (!out.ok) {
      setError(out.message);
      return;
    }
    // The server's own list from here on, not ours.
    const saved = (out.data as { sheet?: { draftContent?: { photoPaths?: unknown } } } | undefined)?.sheet?.draftContent?.photoPaths;
    setOnReport(saved !== undefined ? normaliseReportPhotos(saved) : merged.photos);
    setPicked([]);
    setAdded(true);
  }

  const goRows: BubbleRow[] = [];
  if (block.more > 0) {
    goRows.push({
      id: 'more',
      title: `${block.moreIsFloor ? 'At least ' : ''}${block.more} more on the job`,
      kind: 'more',
      onPress: () => onOpen(block.jobPath),
    });
  }
  goRows.push(open
    ? { id: 'report', title: `Open the job ${document === 'report' ? 'report' : 'sheet'}`, onPress: () => onOpen(`/jobs/${block.jobId}?sheet=${open.sheetId}`) }
    : { id: 'job', title: 'Open the job', onPress: () => onOpen(block.jobPath) });

  const closedReason = target.state === 'none'
    ? "There's no job report on this job yet."
    : target.state === 'issued'
      ? "The job report on this job is already finished, so photos can't be added to it from here."
      : target.state === 'no-photos'
        ? "The job sheet on this job doesn't carry photos, so there's nowhere to add them."
        : null;

  const total = block.photos.length + block.more;

  return (
    <div className="mchat-pp" role="group" aria-label={`${total} job photo${total === 1 ? '' : 's'}`}>
      <div className="mchat-pp-grid">
        {block.photos.map(photo => {
          const strip = onReportByPath.get(photo.path);
          const isOn = picked.includes(photo.path);
          const gone = broken[photo.path] === true;
          const pickable = !!open && live && !busy && !strip && !gone && (isOn || !atRoom);
          const label = [
            photo.aria,
            photo.plot,
            strip ? `already on the ${document}, ${strip}` : null,
            gone ? 'no longer on the job' : null,
            isOn ? 'picked' : null,
          ].filter(Boolean).join(', ');
          const inner = (
            <>
              {gone ? (
                <span className="mchat-pp-gone"><Icon icon={ImageOff} size={18} /></span>
              ) : (
                // eslint-disable-next-line @next/next/no-img-element -- an authenticated app route, not a static asset
                <img
                  className="mchat-pp-img"
                  src={jobPhotoThumbUrl(photo.path)}
                  alt=""
                  loading="lazy"
                  onError={() => setBroken(prev => ({ ...prev, [photo.path]: true }))}
                />
              )}
              <span className="mchat-pp-chip">{photo.plot ?? photo.label}</span>
              {strip && <span className="mchat-pp-on">{strip === 'before' ? 'Before' : 'After'}</span>}
              {isOn && <span className="mchat-pp-tick" aria-hidden="true"><Icon icon={Check} size={12} /></span>}
            </>
          );
          return open ? (
            <button
              key={photo.path}
              type="button"
              className={`mchat-pp-tile${isOn ? ' mchat-pp-tile--on' : ''}${strip ? ' mchat-pp-tile--placed' : ''}`}
              aria-pressed={isOn}
              aria-label={label}
              disabled={!pickable}
              onClick={() => toggle(photo.path)}
            >
              {inner}
            </button>
          ) : (
            <div key={photo.path} className="mchat-pp-tile" role="img" aria-label={label}>{inner}</div>
          );
        })}
      </div>

      {block.videos > 0 && (
        <p className="mchat-pp-note">
          {block.videos === 1 ? '1 video on the job' : `${block.videos} videos on the job`} can&apos;t go on a {document === 'report' ? 'report' : 'job sheet'}.
        </p>
      )}

      {open && picked.length > 0 && (
        <div className="mchat-pp-when" role="radiogroup" aria-label="Which half of the story">
          <span className="mchat-pp-when-label">These are</span>
          {(['before', 'after'] as const).map(w => (
            <button
              key={w}
              type="button"
              role="radio"
              aria-checked={when === w}
              className={`mchat-pp-when-opt${when === w ? ' mchat-pp-when-opt--on' : ''}`}
              disabled={!live || busy}
              onClick={() => setWhen(w)}
            >
              {w === 'before' ? 'Before' : 'After'}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="mchat-body mchat-pp-status" aria-live="polite">
          {error ? (
            <p className="mchat-error" role="alert">{error}</p>
          ) : added && picked.length === 0 ? (
            <p className="mchat-pp-done">
              <Icon icon={Check} size={14} />
              {`Added to the ${document}. The ${document} hasn't been sent.`}
            </p>
          ) : room === 0 && picked.length === 0 ? (
            <p className="mchat-pp-hint">{`The ${document} already has ${REPORT_PHOTO_MAX} photos, which is as many as it takes.`}</p>
          ) : picked.length > 0 && atRoom ? (
            <p className="mchat-pp-hint">{`That's as many as the ${document} takes. It holds ${REPORT_PHOTO_MAX} photos.`}</p>
          ) : (
            <p className="mchat-pp-hint">
              {picked.length === 0 ? 'Tap photos to pick them. ' : ''}Nothing goes to the customer from here.
            </p>
          )}
        </div>
      )}

      {closedReason && <p className="mchat-body mchat-pp-hint">{closedReason}</p>}

      {open && picked.length > 0 && (
        <BubbleActions
          actions={[{
            label: addLabel(picked.length, document),
            busyLabel: 'Adding…',
            busy,
            disabled: !live,
            onPress: () => { void add(); },
          }]}
        />
      )}

      <BubbleRows rows={goRows} />
    </div>
  );
}
